import { useEffect, useState } from 'react'
import { ChevronDown, ChevronRight } from '@untitled-ui/icons-react'
import { Loader } from '@mantine/core'
import dayjs from '@/lib/dayjs'
import { useAxiosApi } from '@/hooks/useAxiosApi'
import { dollars } from './StatTiles'

/** « anthropic/claude-sonnet-5 » → « claude-sonnet-5 ». */
export const shortModel = (m) => m?.split('/').pop() ?? '—'

const Verdict = ({ result, status }) => {
    if (!result) return <span className="text-xs text-gray-300">non jouée</span>

    const { passed, ok_trials, total_trials } = result
    const ratio = total_trials > 1 ? ` ${ok_trials}/${total_trials}` : ''

    // Une capability a le DROIT d'échouer : elle décrit une cible, pas un acquis.
    if (!passed && status === 'capability') {
        return (
            <span className="text-xs text-amber-600">🎯 pas encore{ratio}</span>
        )
    }
    if (!passed) {
        return (
            <span className="text-xs font-medium text-red-500">✗{ratio}</span>
        )
    }
    return (
        <span className="text-xs text-green-600">
            {status === 'capability' ? '🎉 acquise' : '✓'}
            {ratio}
        </span>
    )
}

/**
 * Le scénario d'un cas, tel qu'il est joué : ce que le client écrit. Le fil
 * email est un texte (« [date — CLIENT] … ») ; une conversation chat est un
 * tableau de tours. Un verdict ne se lit pas sans sa question.
 */
const Scenario = ({ transcript, facts, country }) => {
    const [open, setOpen] = useState(false)
    if (!transcript) return null

    let turns
    try {
        const parsed = JSON.parse(transcript)
        if (Array.isArray(parsed)) turns = parsed
    } catch {
        // Fil email : du texte, pas du JSON.
    }

    const text = turns
        ? turns.map((t) => `${t.role === 'assistant' ? 'Assistant' : 'Client'} : ${t.content}`).join('\n\n')
        : transcript
    const expandable = text.length > 240 || !!facts?.trim()

    return (
        <div className="rounded-lg border border-gray-200/70 bg-white px-3 py-2">
            <div className="mb-1 flex items-baseline gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Le cas</span>
                {country && <span className="text-[10px] text-gray-300">pays {country}</span>}
                {expandable && (
                    <button
                        type="button"
                        onClick={() => setOpen(!open)}
                        className="ml-auto text-[10.5px] text-gray-400 underline decoration-gray-200 underline-offset-2 hover:text-gray-700"
                    >
                        {open ? 'réduire' : 'tout voir'}
                    </button>
                )}
            </div>
            <p className={`whitespace-pre-wrap wrap-break-word text-[12px] leading-relaxed text-gray-600 ${open ? '' : 'line-clamp-3'}`}>
                {text}
            </p>
            {open && facts?.trim() && (
                <>
                    <div className="mb-1 mt-2.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                        Faits injectés
                    </div>
                    <p className="whitespace-pre-wrap wrap-break-word text-[12px] leading-relaxed text-gray-500">{facts}</p>
                </>
            )}
        </div>
    )
}

// The deterministic checks (ChatEval / SuggestionsEval), in words. When one
// fails the judge is never consulted, so this is the ONLY explanation a red
// case has — it must read without the grade chips, which compact mode hides.
const failedChecks = (trial) =>
    Object.entries(trial.grades || {})
        .filter(([, ok]) => ok === false)
        .map(([key]) => {
            // chat
            if (key === 'handover') return 'renvoi vers l’email contraire à l’attendu'
            if (key === 'markers') return 'marqueur mal placé'
            // email
            if (key === 'action') return `action « ${trial.action} » contraire à l’attendu`
            if (key === 'no_markdown') return 'brouillon en markdown'
            if (key === 'error') return 'erreur pendant la génération'
            // both
            if (key === 'grounding') return null // the judge's own text says it
            if (key.startsWith('absent:')) return `contient « ${key.slice(7)} »`
            if (key.startsWith('présent:')) return `ne contient pas « ${key.slice(8)} »`
            return key
        })
        .filter(Boolean)

/**
 * Un essai : la raison du juge d'abord (en français, c'est la lecture du
 * responsable service client), le brouillon produit derrière un clic.
 * `compact` masque la mécanique (grades, action) qui ne parle qu'à Thomas.
 */
const Trial = ({ trial, compact, showIndex }) => {
    const [showDraft, setShowDraft] = useState(false)
    const [showReason, setShowReason] = useState(false)

    return (
        <div
            className={`rounded-lg px-3 py-2 ${
                trial.passed ? 'bg-gray-50' : 'bg-red-50/60'
            }`}
        >
            <div className="flex flex-wrap items-center gap-2 text-[10px] text-gray-500">
                {showIndex && (
                    <span className="font-mono">essai {trial.index}</span>
                )}
                {!compact && <span>action {trial.action}</span>}
                {trial.finish_reason === 'length' && (
                    <span className="text-amber-600">⚠ tronqué</span>
                )}
                {!compact &&
                    Object.entries(trial.grades || {}).map(([k, v]) => (
                        <span
                            key={k}
                            className={v ? 'text-green-600' : 'text-red-500'}
                        >
                            {v ? '✓' : '✗'} {k}
                        </span>
                    ))}
                <div className="ml-auto flex items-center gap-3">
                    {trial.judge_reason && trial.judge_summary && (
                        <button
                            type="button"
                            onClick={() => setShowReason(!showReason)}
                            className="text-gray-400 underline decoration-gray-200 underline-offset-2 hover:text-gray-700"
                        >
                            {showReason ? 'masquer l’analyse' : 'voir l’analyse'}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => setShowDraft(!showDraft)}
                        className="text-gray-400 underline decoration-gray-200 underline-offset-2 hover:text-gray-700"
                    >
                        {showDraft ? 'masquer le brouillon' : 'voir le brouillon'}
                    </button>
                </div>
            </div>
            {trial.judge_summary || trial.judge_reason ? (
                <>
                    {/* Le constat en UNE phrase, lisible sans rien déplier ;
                        l'analyse complète du juge reste derrière un clic. */}
                    <p className="mt-1.5 text-[12.5px] leading-relaxed text-gray-800">
                        <span
                            className={
                                trial.judge_verdict === 'ok'
                                    ? 'text-green-600'
                                    : trial.judge_verdict === 'ko'
                                      ? 'text-red-500'
                                      : 'text-gray-400'
                            }
                        >
                            {trial.judge_verdict === 'ok' ? '✓ ' : trial.judge_verdict === 'ko' ? '✗ ' : '? '}
                        </span>
                        {trial.judge_summary ?? trial.judge_reason}
                    </p>
                    {showReason && trial.judge_reason && (
                        <p className="mt-1.5 border-l-2 border-gray-200 pl-2.5 text-[12px] leading-relaxed text-gray-500">
                            {trial.judge_reason}
                        </p>
                    )}
                </>
            ) : failedChecks(trial).length ? (
                /* A check failed before the judge: say which, in words — the
                   only reason this case has. */
                <p className="mt-1.5 text-[12.5px] leading-relaxed text-gray-800">
                    <span className="text-red-500">✗ </span>
                    {failedChecks(trial).join(' · ')}
                    <span className="text-gray-400"> — le juge n’a pas été consulté</span>
                </p>
            ) : (
                <p className="mt-1.5 text-[11px] text-gray-400">
                    {trial.action === 'none'
                        ? 'Aucun brouillon proposé (escalade).'
                        : 'Pas de critère de juge sur ce cas : seuls le format et l’action sont vérifiés.'}
                </p>
            )}
            {showDraft && (
                <pre className="mt-2 whitespace-pre-wrap wrap-break-word border-t border-gray-200/70 pt-2 font-mono text-[11px] leading-relaxed text-gray-600">
                    {trial.draft || '(vide)'}
                </pre>
            )}
        </div>
    )
}

/**
 * Détail d'un run d'éval, cas par cas : pour chaque cas son verdict, la raison
 * du juge et le brouillon produit. Un score ne se lit pas seul — c'est en
 * relisant qu'on repère un cas mal spécifié ou une consigne mal écrite.
 */
export default function EvalRunDetail({ runId, compact = false }) {
    const { requestGet } = useAxiosApi()
    const [detail, setDetail] = useState(null)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (!runId) return
        let active = true
        setLoading(true)
        requestGet(`/support/ai/evals/runs/${runId}`)
            .then(({ data }) => active && setDetail(data))
            .catch(() => active && setDetail(null))
            .finally(() => active && setLoading(false))
        return () => {
            active = false
        }
    }, [runId])

    if (!runId) return null
    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <Loader size={16} />
            </div>
        )
    }
    if (!detail) {
        return (
            <p className="py-4 text-xs text-gray-400">Détail indisponible.</p>
        )
    }

    const { run, tasks } = detail
    const failed = run.tasks_passed < run.tasks_total

    // Un run sans fin (requête coupée, worker tué) n'a pas de score : ses
    // compteurs affichés tels quels feraient croire à un échec massif.
    if (run.state === 'interrupted') {
        return (
            <div className="rounded-2xl border border-amber-200/70 bg-amber-50/50 px-5 py-4">
                <p className="text-[13px] font-medium text-gray-900">Test interrompu</p>
                <p className="mt-1 text-[12px] leading-relaxed text-gray-600">
                    Démarré le {dayjs(run.started_at).format('D MMM YYYY [à] HH:mm')} ({shortModel(run.model)}), il ne s'est
                    jamais terminé : {tasks.length} cas joué{tasks.length > 1 ? 's' : ''} sur {run.tasks_total}. Ses compteurs
                    ne veulent rien dire, il faut le relancer.
                </p>
            </div>
        )
    }

    return (
        <div className="rounded-2xl border border-gray-200/70 bg-white">
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-b border-gray-100 px-5 py-3">
                <span
                    className={`text-2xl font-semibold tabular-nums ${
                        failed ? 'text-red-500' : 'text-green-600'
                    }`}
                >
                    {run.tasks_passed}
                    <span className="text-gray-300">/</span>
                    {run.tasks_total}
                </span>
                <span className="text-xs text-gray-500">
                    cas réussis · {shortModel(run.model)}
                    {run.prompt_version_id && ` · fiche de poste v${run.prompt_version_id}`}
                    {run.trials_per_task > 1 &&
                        ` · ${run.trials_per_task} essais par cas`}
                </span>
                <span className="ml-auto text-[11px] text-gray-400">
                    {run.cost != null && `${dollars(run.cost)} · `}
                    {run.duration_s != null && `${run.duration_s} s · `}
                    {dayjs(run.started_at).fromNow()}
                </span>
            </div>
            <div className="flex flex-col divide-y divide-gray-100">
                {tasks.map((t) => (
                    <div key={t.name} className="px-5 py-3">
                        <div className="flex items-center gap-2">
                            <span className="text-[13px] text-gray-800">
                                {t.name}
                            </span>
                            <span className="ml-auto">
                                <Verdict result={t} status={t.status} />
                            </span>
                        </div>
                        <div className="mt-2 flex flex-col gap-2">
                            <Scenario transcript={t.transcript} facts={t.facts} country={t.country} />
                            {t.trials.map((tr) => (
                                <Trial
                                    key={tr.index}
                                    trial={tr}
                                    compact={compact}
                                    showIndex={t.trials.length > 1}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}
